import { Image, Platform, Text, View } from "react-native";
import { COLORS } from "../../lib/brand";

const AMBER = COLORS.AMBER.hex;
const FOREST = COLORS.FOREST.hex;

/** Up to two letters from the name. Single-word names get the first two
 *  letters; multi-word names get first letter of first + last token. */
function initials(name?: string): string {
  if (!name?.trim()) return "?";
  const p = name.trim().split(/\s+/);
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return `${p[0][0]}${p[p.length - 1][0]}`.toUpperCase();
}

/**
 * Bare-initials Coravu avatar.
 *
 * The vast majority of students never upload a profile photo, so the
 * fallback has to look as deliberate as the photo case. Uses Cormorant
 * Garamond for the initials in amber on a forest disc, with a soft
 * inner border + drop shadow so the disc reads as a designed token, not
 * a CSS placeholder. The dark theme variant flips the bg to an amber
 * tinted disc on a darker surface (used in the topbar AC/AM idiom).
 */
export function Avatar({
  name,
  imageUrl,
  size = "md",
  theme = "light",
}: {
  name?: string;
  imageUrl?: string | null;
  size?: "sm" | "md" | "lg";
  theme?: "light" | "dark";
}) {
  const dim = size === "sm" ? 28 : size === "lg" ? 40 : 30;
  const textSize = size === "sm" ? 12 : size === "lg" ? 16.5 : 13.5;
  if (imageUrl) {
    return (
      <Image
        source={{ uri: imageUrl }}
        style={{
          width: dim,
          height: dim,
          borderRadius: dim / 2,
        }}
      />
    );
  }

  const isDark = theme === "dark";
  return (
    <View
      style={[
        {
          width: dim,
          height: dim,
          borderRadius: dim / 2,
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: isDark ? "rgba(181,135,58,0.18)" : FOREST,
          borderWidth: 1,
          borderColor: isDark ? "rgba(181,135,58,0.45)" : "rgba(181,135,58,0.55)",
        },
        Platform.OS === "web"
          ? ({
              boxShadow:
                "inset 0 0 0 1px rgba(247,244,238,0.06), 0 1px 2px rgba(28,43,26,0.18)",
            } as object)
          : null,
      ]}
    >
      <Text
        style={{
          fontFamily: "CormorantGaramond_600SemiBold",
          fontSize: textSize,
          // Slight letter-spacing tightens the two-letter pair so it sits
          // optically centered inside the disc.
          letterSpacing: 0.6,
          color: AMBER,
          // Nudge the baseline down half a pixel so Cormorant looks
          // visually centered (its baseline sits a hair high).
          marginTop: 1,
        }}
      >
        {initials(name)}
      </Text>
    </View>
  );
}
